import { CheckCircleIcon, IdentificationIcon, ArrowsRightLeftIcon, FaceSmileIcon, InformationCircleIcon, ArrowLeftIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useLocalizedStrings } from "../../Localization";
import { AidKitLogo, BarSpinner, canReturnToApplication, returnToApplication, SpacedSpinner, useMarkdown } from "../../Util";
import { facingModeForStage } from "./consts";
import { useEffect, useState } from "react";
import { useLivenessDetectionSessionUIContext } from "./SessionUIController";

type FlowState = "pending" | "failed" | "passed" | "max_attempts_reached" | undefined;

// FinalPage can update flow state for an individual part to "uploading" for more specific localization
type FinalFlowState = "uploading" | "failed_to_upload" | FlowState;

type Part = 'front' | 'back' | 'selfie';

type LivenessPartStatusProps = {
  part: Part;
  multipleCameras: boolean;
  setRetrying: React.Dispatch<React.SetStateAction<boolean>>;
};

function usePartActions(part: Part, multipleCameras: boolean, setRetrying: React.Dispatch<React.SetStateAction<boolean>>) {
  const { livenessProgress, livenessConfig, realtimeResults, setRealtimeResults, setActionState, setFacingMode, setLivenessProgress } = useLivenessDetectionSessionUIContext();

  let partState = realtimeResults?.[part]?.state as FinalFlowState;
  if (livenessProgress[part].videoUpload !== 1 || livenessProgress[part].imageUpload !== 1) {
    partState = 'uploading';
  }
  if (livenessProgress[part]?.failedToUpload) {
    partState = 'failed_to_upload';
  }

  const uploadPercent = Math.round(
    Math.min(livenessProgress[part].imageUpload || 0, livenessProgress[part].videoUpload || 0) * 100,
  );

  const retryPart = (target: Part) => {
    setRetrying(true);
    setLivenessProgress((cur) => ({ ...cur, [target]: { ...cur[target], failedToUpload: false, imageUpload: 0, videoUpload: 0 } }));
    setRealtimeResults((cur) => ({ ...cur, [target]: { state: "pending" } }));
    setActionState(target);
    if (multipleCameras) setFacingMode(facingModeForStage[target]);
  };

  const onRetry = () => retryPart(part);

  return { partState, uploadPercent, livenessConfig, onRetry };
}

function LivenessPartStatus({ part, multipleCameras, setRetrying }: LivenessPartStatusProps) {
  const { partState, uploadPercent, onRetry } = usePartActions(part, multipleCameras, setRetrying);
  const L = useLocalizedStrings();

  const partIcon = {
    front: <IdentificationIcon className="w-6 h-6 text-gray-600" />,
    back: <ArrowsRightLeftIcon className="w-6 h-6 text-gray-600" />,
    selfie: <FaceSmileIcon className="w-6 h-6 text-gray-600" />,
  }[part];

  const isFailed = partState === "failed" || partState === "failed_to_upload";
  const containerClass = isFailed
    ? "flex items-center justify-between gap-x-2 bg-amber-50 rounded-xl p-4 border border-amber-200"
    : "flex items-center justify-between gap-x-2 bg-gray-50 rounded-xl p-4 border border-gray-200";
  return (
    <div className={containerClass}>
      <div className="flex items-center gap-3 shrink-0">
        <div className="shrink-0 flex justify-center">{partIcon}</div>
        <span className="font-semibold text-gray-900 shrink-0">
          {part === "front" ? L.liveness.part_front : part === "back" ? L.liveness.part_back : L.liveness.part_selfie}
        </span>
      </div>
      <div className="text-sm text-gray-800 flex-1 min-w-0 text-center">
        {(() => {
          switch (partState) {
            case "failed":
            case "passed":
            case "max_attempts_reached": return null;
            case "uploading": return <span className="text-gray-700">{L.questions.attachment.uploading} {uploadPercent}%</span>;
            case "failed_to_upload": return <span className="text-red-700">{L.liveness.failed_to_upload}</span>;
            case "pending":
            default: return (
              <span className="inline-flex items-center gap-1 text-gray-600 justify-center">
                <SpacedSpinner />
                {L.questions.identity.checking_quality}
              </span>
            );
          }
        })()}
      </div>
      <div className="flex justify-end shrink-0">
        {(() => {
          switch (partState) {
            case "failed_to_upload":
            case "failed": return (
              <button type="button" className="rounded-md bg-indigo-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-800" onClick={onRetry}>
                {L.liveness.retake}
              </button>
            );
            case "passed":
            case "max_attempts_reached": return (
              <span className="text-green-600 flex items-center gap-1 font-medium text-sm">
                <CheckCircleIcon className="w-5 h-5" />
                {L.questions.identity.complete}
              </span>
            );
            case "pending":
            default: return <SpacedSpinner />;
          }
        })()}
      </div>
    </div>
  );
}

export function FinalPage(props: {
  multipleCameras: boolean,
  canCloseWindow: boolean,
  setRetrying: React.Dispatch<React.SetStateAction<boolean>>,
}) {

  const { livenessProgress, livenessConfig, realtimeResults, setActionState, surveyUrl } = useLivenessDetectionSessionUIContext();
  const L = useLocalizedStrings();
  const almostThereBody = useMarkdown(L.liveness.almost_there_body.replace('$retake', '**' + L.liveness.retake + '**'), false, true);

  const parts = livenessConfig!.identification ?
    ['front', 'back', 'selfie'] as const :
    ['selfie'] as const;

  // Figure out the global state to display
  const [state, setState] = useState(L.questions.identity.please_wait_processing);
  const [hasFailures, setHasFailures] = useState(false);
  useEffect(() => {
    let state = L.questions.identity.please_wait_processing;
    let completeCount = 0;
    let failedCount = 0;
    // console.log({ livenessProgress, realtimeResults });
    parts.forEach((part) => {
      if ((livenessProgress)[part].videoUpload !== 1 || (livenessProgress)[part].imageUpload !== 1) return;
      if ((realtimeResults)?.[part]?.state === 'passed' || (realtimeResults)?.[part]?.state === 'max_attempts_reached') completeCount += 1;
      if ((realtimeResults)?.[part]?.state === 'failed') failedCount += 1;
    });
    console.log("FCT:", failedCount, completeCount, parts.length);

    if (completeCount === parts.length) {
      state = props.canCloseWindow
        ? L.applicant.all_done_you_may_now_close_this_window
        : L.questions.identity.please_wait_processing;
      setActionState("done");
    }
    if (failedCount > 0) {
      state = L.questions.identity.blurry_sorry;
      setHasFailures(true);
    } else {
      setHasFailures(false);
    }
    setState(state);
  }, [props.canCloseWindow, livenessProgress, realtimeResults]);

  const partsList = parts.map((part) => (
    <LivenessPartStatus key={part} part={part} multipleCameras={props.multipleCameras} setRetrying={props.setRetrying} />
  ));

  const doneHeading = L.liveness.completion_heading;
  const showSuccessChrome = state === L.applicant.all_done_you_may_now_close_this_window;
  return (
    <div className="bg-gray-100 flex flex-col fixed h-screen w-screen overflow-y-scroll" style={{ height: "-webkit-fill-available" }}>
      <div className="flex flex-col items-center py-6 px-6 w-full max-w-lg mx-auto">
        {showSuccessChrome ? (
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-12 h-12 text-green-600" strokeWidth={2.5} />
              </div>
              <div className="absolute inset-0 w-20 h-20 bg-green-200/30 rounded-full animate-ping pointer-events-none" />
            </div>
          </div>
        ) : hasFailures ? (
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-amber-600" strokeWidth={2} />
            </div>
          </div>
        ) : null}
        <div className="text-center mb-6 w-full">
          {showSuccessChrome ? (
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{doneHeading}</h1>
          ) : hasFailures ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{L.liveness.almost_there}</h1>
              <div className="text-gray-700 [&_p]:m-0 [&_strong]:font-semibold [&_strong]:text-gray-900">{almostThereBody}</div>
            </>
          ) : (
            <>
              <AidKitLogo width={100} height={50} />
              <h3 className="text-xl mt-4 text-gray-900">{state}</h3>
              <div className="mt-2">
                {state === L.questions.identity.please_wait_processing ? <BarSpinner /> : null}
              </div>
            </>
          )}
        </div>
        <div className="w-full space-y-3 mb-6">{partsList}</div>
        {state === L.applicant.all_done_you_may_now_close_this_window ? (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 w-full mb-6">
            <div className="flex items-start gap-2">
              <InformationCircleIcon className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-blue-900 mb-2">{L.liveness.what_next_title}</div>
                <p className="text-sm text-blue-900 leading-relaxed">{L.liveness.what_next_body}</p>
              </div>
            </div>
          </div>
        ) : null}
        {state === L.applicant.all_done_you_may_now_close_this_window && canReturnToApplication(surveyUrl) ? (
          <button
            type="button"
            className="w-full mt-2 bg-indigo-900 text-white font-semibold py-3 rounded-lg hover:bg-indigo-800 flex items-center justify-center gap-2"
            onClick={(e) => {
              e.preventDefault();
              returnToApplication(surveyUrl);
            }}
          >
            <ArrowLeftIcon className="w-5 h-5" />
            {L.liveness.return_to_application}
          </button>
        ) : null}
      </div>
    </div>
  );
}
